// The command line as curl itself would have been handed it, read back into the list the builder draws. Nothing is
// canonicalised on the way through: an option keeps the spelling it was written in and an operand keeps its place,
// so a pasted command comes back out of `writeCurl` as the same command.

import type { Command, Entry } from "./entries";
import { findLong, findShort, URL_FLAG } from "./options";
import { splitWords, type Word } from "./shell";

export const NOT_CURL = "The command has to start with curl";
export const MANY_COMMANDS = "One command at a time: take off the pipe, the redirect or the --next";

export function parseCurl(source: string): Command {
  const { words, error, shell } = splitWords(source);
  if (error) return { entries: [], error, shell };

  let at = 0;

  // A command copied out of a terminal brings the prompt with it, which is one character to drop and not an error.
  if (words[at] && !words[at].quoted && PROMPTS.has(words[at].text)) at += 1;

  // Nothing typed yet is not a command that is wrong; the builder opens empty and writes the first one.
  if (at >= words.length) return { entries: [], error: null, shell };
  if (!CURL.test(words[at].text)) return { entries: [], error: NOT_CURL, shell };
  at += 1;

  const entries: Entry[] = [];

  // The value an option takes is the next word, which a shell operator never is: `-o | tee` writes to no file.
  const takeValue = (): Value => {
    const next = words[at];
    if (!next || isOperator(next)) return { value: "" };
    at += 1;
    return valueOf(next.text, next);
  };

  while (at < words.length) {
    const word = words[at];
    at += 1;

    if (isOperator(word)) return { entries: [], error: MANY_COMMANDS, shell };

    const { text } = word;

    if (text.startsWith("--")) {
      // curl reads `--data=x` as well as `--data x`, and the builder writes the second of the two back.
      const split = text.indexOf("=");
      const flag = split < 0 ? text : text.slice(0, split);
      const inline = split < 0 ? null : valueOf(text.slice(split + 1), word);

      if (flag === URL_FLAG) {
        entries.push({ kind: "url", ...(inline ?? takeValue()), flag });
        continue;
      }

      const spec = findLong(flag);
      if (!spec) {
        entries.push({ kind: "unknown", flag });
        continue;
      }

      if (spec.value === "none") {
        entries.push({ kind: "option", name: spec.name, flag, value: "" });
        continue;
      }

      entries.push({ kind: "option", name: spec.name, flag, ...(inline ?? takeValue()) });
      continue;
    }

    // A bundle is read letter by letter, and the first that wants a value takes the rest of the bundle as one.
    if (text.startsWith("-") && text.length > 1) {
      let cursor = 1;

      while (cursor < text.length) {
        const letter = text[cursor];
        cursor += 1;

        const spec = findShort(letter);
        if (!spec) {
          entries.push({ kind: "unknown", flag: `-${letter}` });
          continue;
        }

        if (spec.value === "none") {
          entries.push({ kind: "option", name: spec.name, flag: `-${letter}`, value: "" });
          continue;
        }

        const rest = text.slice(cursor);
        const value = rest === "" ? takeValue() : valueOf(rest, word);
        entries.push({ kind: "option", name: spec.name, flag: `-${letter}`, ...value });
        cursor = text.length;
      }

      continue;
    }

    // Everything else is an operand, which for curl is a URL — a lone `-` among them being standard input.
    entries.push({ kind: "url", ...valueOf(text, word), flag: null });
  }

  return { entries, error: null, shell };
}

interface Value {
  value: string;
  variables?: string[];
}

// The word's references go with the value they stand in, and only where there are any, which is seldom.
function valueOf(value: string, word: Word): Value {
  return word.variables.length === 0 ? { value } : { value, variables: word.variables };
}

// The name curl is invoked under, however the path to it was written and whatever a Windows shell calls the file.
const CURL = /(^|[/\\])curl(\.exe)?$/i;

const PROMPTS = new Set(["$", "%"]);

// Only a word that stands alone and unquoted is one of these: the `|` inside a JSON body is a character of the body.
const OPERATORS = new Set(["|", "||", "&&", ";", "&", "<", ">", ">>", "2>", "2>&1", "&>", "--next", "-:"]);

function isOperator(word: Word): boolean {
  return !word.quoted && OPERATORS.has(word.text);
}
